/**
 * Locale utility functions for number formatting and parsing.
 * Shared across components and services that handle locale-specific numeric operations.
 *
 * NEW CODE SHOULD USE `./locale-number.ts` (`formatMiqNumber` / `parseMiqNumber`). The
 * `formatNumberValue` / `formatNumberValueForLocale` / `normalizePolishNumber` helpers at the
 * bottom of this file are LEGACY and each has known defects (documented at each one); they are left
 * untouched because ~190 display call sites depend on their exact output and are retired in a
 * later phase.
 */
import { CE_DOT_COMMA_LOCALES, CE_SPACE_COMMA_LOCALES } from './ce-locales.const';
import { getTokensFromLocalStorage } from '../storage-util';

/*
 * L3: the number-format locale partition used to be DUPLICATED here (as `Set`s) and in
 * `@miq/shared-models` (as arrays). It now has exactly one definition, in `./ce-locales.const`,
 * and both shapes are re-exported from here so existing importers of this module are unaffected.
 */
export {
  CE_COMMA_SPACE_DECIMAL_LOCALES,
  CE_COMMA_DOT_DECIMAL_LOCALES,
  CE_LOCALES,
  CE_SPACE_COMMA_LOCALES,
  CE_DOT_COMMA_LOCALES,
} from './ce-locales.const';

/**
 * Supported Angular locale codes
 */
export enum SupportedLocale {
  EN_US = 'en-US',
  PL_PL = 'pl-PL',
  // Space+comma CE locales (thousands = space/NBSP, decimal = comma)
  FI_FI = 'fi-FI',
  HU_HU = 'hu-HU',
  NB_NO = 'nb-NO',
  SV_SE = 'sv-SE',
  PT_PT = 'pt-PT',
  CS_CZ = 'cs-CZ',
  SK_SK = 'sk-SK',
  FR_FR = 'fr-FR',
  FR_BE = 'fr-BE',
  FR_LU = 'fr-LU',
  FR_CH = 'fr-CH',
  // Dot+comma CE locales (thousands = dot, decimal = comma)
  DE_DE = 'de-DE',
  DE_AT = 'de-AT',
  IT_IT = 'it-IT',
  NL_NL = 'nl-NL',
  DA_DK = 'da-DK',
  ES_ES = 'es-ES',
  RO_RO = 'ro-RO',
  SR_RS = 'sr-RS',
  NL_BE = 'nl-BE',
  DE_BE = 'de-BE',
  DE_LU = 'de-LU',
  DE_CH = 'de-CH',
  IT_CH = 'it-CH',
}

/**
 * Storage key for locale ID in localStorage
 */
const LOCALE_ID_STORAGE_KEY = 'localeId';

/**
 * Gets the current locale ID from storage, HONOURING IMPERSONATION.
 *
 * !! This used to read `localStorage['localeId']` directly, which was WRONG while impersonating:
 * both writers (`@miq/platform` `UserService.setDefaultLang` and
 * `UserSessionService.getUserLocale`) write to `imperson_localeID` instead of `localeId` when
 * `isImpersonationModeOn`, so every number and date in the app followed the IMPERSONATOR's locale
 * rather than the impersonated user's. `getTokensFromLocalStorage()` already resolves that
 * indirection for `expires_in` / `userid_token` / `userInfo`, so it is reused here rather than
 * re-implementing the key choice (which is exactly how the two drifted apart in the first place).
 *
 * VALUE SHAPE: both writers go through `AppStorageService.setItem`, which `JSON.stringify`s its
 * argument. For a number that yields the bare digits (`JSON.stringify(15) === '15'`), so `parseInt`
 * reads it correctly. The quote strip below additionally tolerates a value that was stored as a
 * JSON *string* (`'"15"'`), which `parseInt` alone would reject.
 *
 * @returns The numeric locale ID, or null if not found or invalid
 *
 * @example
 * const localeId = getLocaleIdFromStorage(); // returns 15 if stored
 */
export function getLocaleIdFromStorage(): number | null {
  // In non-impersonation mode this IS `localStorage`; while impersonating it is a plain object whose
  // `localeId` comes from `imperson_localeID`.
  const storedLocaleId = getTokensFromLocalStorage()[LOCALE_ID_STORAGE_KEY];
  if (!storedLocaleId) {
    return null;
  }
  const parsed = parseInt(String(storedLocaleId).replace(/"/g, ''), 10);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Gets the current Angular locale code from localStorage.
 * Convenience function that combines getLocaleIdFromStorage() and mapLocaleIdToAngularLocale().
 *
 * @returns Angular locale code (e.g., 'pl-PL', 'en-US', 'de-DE')
 * @default 'en-US' if no locale is stored
 *
 * @example
 * const locale = getCurrentLocale(); // returns 'pl-PL' if localeId 15 is stored
 */
export function getCurrentLocale(): string {
  const localeId = getLocaleIdFromStorage();
  return mapLocaleIdToAngularLocale(localeId);
}

/**
 * Maps user locale ID to Angular locale codes for proper number/date formatting.
 * CE country English locales map to their country's native format
 * (e.g. FinlandEnglish → fi-FI, so Finnish number format is used).
 */
export function mapLocaleIdToAngularLocale(localeId: number | null | undefined): string {
  const localeMap: { [key: number]: string } = {
    1: 'en-US', // US
    3: 'zh-CN', // ChinaChinese
    4: 'en-CN', // ChinaEnglish
    5: 'en-IN', // IndiaEnglish
    7: 'en-GB', // UkEnglish
    8: 'nl-NL', // NlEnglish        → Dutch number format (dot thousands, comma decimal)
    9: 'pt-PT', // Portugal         → Portuguese number format (space thousands, comma decimal)
    10: 'it-IT', // ItalyEnglish    → Italian number format (dot thousands, comma decimal)
    11: 'fi-FI', // FinlandEnglish  → Finnish number format (space thousands, comma decimal)
    12: 'sv-SE', // SwedenEnglish   → Swedish number format (space thousands, comma decimal)
    13: 'da-DK', // DenmarkEnglish  → Danish number format (dot thousands, comma decimal)
    14: 'nb-NO', // NorwayEnglish   → Norwegian number format (space thousands, comma decimal)
    15: 'pl-PL', // PolandEnglish   → Polish number format (space thousands, comma decimal)
    16: 'de-AT', // AustriaEnglish  → Austrian number format (dot thousands, comma decimal)
    17: 'hu-HU', // Hungary         → Hungarian number format (space thousands, comma decimal)
    18: 'ro-RO', // Romania         → Romanian number format (dot thousands, comma decimal)
    21: 'es-ES', // Spain           → Spanish number format (dot thousands, comma decimal)
    26: 'de-DE', // Germany         → German number format (dot thousands, comma decimal)
    28: 'en-AU', // AustraliaEnglish
    29: 'en-NZ', // NewZealandEnglish
    31: 'pl-PL', // PolandPolish    → Polish number format (space thousands, comma decimal)
    32: 'it-IT', // ItalyItalian    → Italian number format (dot thousands, comma decimal)
    33: 'sv-SE', // SwedenSwedish   → Swedish number format (space thousands, comma decimal)
    34: 'fi-FI', // FinlandFinnish  → Finnish number format (space thousands, comma decimal)
    35: 'nb-NO', // NorwayNorwegian → Norwegian number format (space thousands, comma decimal)
    37: 'nl-NL', // NlDutch         → Dutch number format (dot thousands, comma decimal)
    38: 'pt-PT', // Portuguese      → Portuguese number format (space thousands, comma decimal)
    // 39: 'zh-HK', // HongKongChinese — locale deprecated, falls back to en-US
    40: 'en-HK', // HongKongEnglish
    47: 'es-ES', // Spanish         → Spanish number format (dot thousands, comma decimal)
    48: 'en-IE', // Ireland
    49: 'de-AT', // AustriaGerman   → Austrian number format (dot thousands, comma decimal)
    50: 'de-DE', // GermanyGerman   → German number format (dot thousands, comma decimal)
    53: 'ko-KR', // KoreaKorean
    54: 'en-KR', // KoreaEnglish
  };
  const result = (localeId != null ? localeMap[localeId] : undefined) ?? SupportedLocale.EN_US;
  return result;
}

/**
 * Normalize numeric string based on locale for parsing.
 * Converts locale-specific formatting to standard format for Number.parseFloat/parseInt.
 *
 * - Space+comma CE (pl-PL, fi-FI, hu-HU, nb-NO, sv-SE, pt-PT): removes spaces, replaces comma with dot
 * - Dot+comma CE (de-DE, de-AT, it-IT, nl-NL, da-DK, es-ES, ro-RO): removes dots, replaces comma with dot
 * - Other locales (en-US etc.): removes commas
 *
 * @example
 * normalizeNumericString('1 234 567,89', 'pl-PL'); // returns '1234567.89'
 * normalizeNumericString('1.234.567,89', 'de-DE'); // returns '1234567.89'
 * normalizeNumericString('1,234,567.89', 'en-US'); // returns '1234567.89'
 */
export function normalizeNumericString(val: string, locale: string): string {
  if (CE_SPACE_COMMA_LOCALES.has(locale)) {
    // Remove spaces/NBSP (thousands separator) and replace comma with dot (decimal separator)
    return val.replace(/[\s\u00A0\u202F]/g, '').replace(/,/g, '.');
  }
  if (CE_DOT_COMMA_LOCALES.has(locale)) {
    // Remove dots (thousands separator) and replace comma with dot (decimal separator)
    return val.replace(/\./g, '').replace(/,/g, '.');
  }
  // For other locales (en-US etc.), remove commas (thousands separator)
  return val.replace(/,/g, '');
}
/**
 * !! LEGACY — prefer `parseMiqNumber` (`./locale-number.ts`). !!
 *
 * Two defects, both left in place because `ce-range-item.component.ts` depends on the exact output:
 *  1. NOT symmetric with the partition: the `else` branch applies the SPACE+COMMA rule to EVERY
 *     non-dot-comma locale, including `en-US` — so `'1,234.56'` becomes `1.234` (it replaces the
 *     first comma with a dot and never strips the US thousands comma).
 *  2. `replace(',', '.')` replaces only the FIRST comma, so `'1,234,56'` mis-parses.
 */
export function normalizePolishNumber(value: number | string): number | string {
  if (value === null || value === undefined || value === '') return value;
  const strValue = String(value);
  const locale = getCurrentLocale();
  if (CE_DOT_COMMA_LOCALES.has(locale)) {
    // Dot thousands, comma decimal: "1.234,56" → "1234.56"
    const normalized = strValue.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(normalized);
    return isNaN(num) ? value : num;
  }
  // Space/NBSP thousands, comma decimal: "1 234,56" → "1234.56"
  const normalized = strValue.replace(/[\u00A0\u0020]/g, '').replace(',', '.');
  const num = parseFloat(normalized);
  return isNaN(num) ? value : num;
}

/**
 * !! LEGACY — prefer `formatMiqNumber` (`./locale-number.ts`). !!
 * @see formatNumberValueForLocale for the en-US defect this inherits.
 */
export function formatNumberValue(value: number | string): number | string {
  return formatNumberValueForLocale(value, getCurrentLocale());
}

/**
 * !! LEGACY — prefer `formatMiqNumber` (`./locale-number.ts`). !!
 *
 * KNOWN DEFECT (not fixed here; `deltas-table.component.ts` is its only caller and passes a CE
 * locale): there is no `comma-dot` branch, so a NON-CE locale falls through to the space+comma
 * branch and a US user is shown `1 234,56`. `formatMiqNumber` special-cases `comma-dot` explicitly
 * so it cannot reproduce this.
 */
export function formatNumberValueForLocale(value: number | string, locale: string): number | string {
  if (value === null || value === undefined || value === '') return value;
  const strValue = typeof value === 'string' ? value : String(value);
  const normalized = strValue.replace(/[\u00A0\u0020\u202F]/g, '').replace(',', '.');
  const num = parseFloat(normalized);
  if (isNaN(num)) return value;
  const decimalPart = normalized.split('.')[1];
  const decimalCount = decimalPart ? decimalPart.length : 0;
  const fixed = num.toFixed(decimalCount);
  const [intPart, fracPart] = fixed.split('.');
  if (CE_DOT_COMMA_LOCALES.has(locale)) {
    // Dot+comma CE format: 1.234,56
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return fracPart !== undefined ? `${formattedInt},${fracPart}` : formattedInt;
  }
  // Space+comma CE format (pl-PL and all other space-group locales): 1 234,56
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
  return fracPart !== undefined ? `${formattedInt},${fracPart}` : formattedInt;
}
